// src/services/contract/ContractService.ts
// Verwaltet den Vertrags-Upload pro Kunde.
// - Privater Bucket "customer-contracts", Zugriff nur fuer angemeldete Mitarbeiter (RLS).
// - Download ueber signierte Links.
// - Ein Dokument pro Kunde: ein neuer Upload ersetzt den alten (altes Dokument wird entfernt).
//
// Ausnahme seit Einfuehrung der digitalen Vertraege: Dokumente, die das
// Vertragswesen erzeugt hat, liegen unter dem Praefix "{customerId}/vertraege/"
// und werden NIEMALS physisch geloescht. Ein Upload oder ein Loeschklick loest
// bei diesen nur den Zeiger am Kunden, die Datei selbst bleibt liegen.
// Grund: Der Bucket hat keine Versionierung. Ein unterschriebener Vertrag waere
// sonst durch einen einzigen Klick unwiederbringlich weg. Die Datei bleibt
// ueber public.vertraege referenziert und damit erreichbar.

import { supabase } from '../../shared/services/supabase';

const BUCKET = 'customer-contracts';
const MAX_FILE_SIZE_MB = 25;

// Pfadbestandteil, unter dem das Vertragswesen erzeugte und signierte
// Dokumente ablegt. Dient als Schutzmarkierung gegen physisches Loeschen.
const GENERIERT_PRAEFIX = '/vertraege/';

export interface CustomerContract {
  storagePath: string;
  dateiname: string;
  hochgeladenAm: string | null;
}

export interface ContractActionResult {
  success: boolean;
  error?: string;
}

class ContractService {
  /**
   * Bereinigt den Dateinamen fuer den Storage-Schluessel (keine Umlaute/Sonderzeichen).
   * Der Originalname wird separat am Kunden gespeichert.
   */
  private sanitizeFilename(filename: string): string {
    return filename
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9._-]/g, '_');
  }

  /**
   * Stammt das Dokument aus dem Vertragswesen (erzeugt oder signiert)?
   * Solche Dateien duerfen nicht aus dem Storage entfernt werden.
   */
  private istGenerierterVertrag(storagePath: string | null | undefined): boolean {
    return !!storagePath && storagePath.includes(GENERIERT_PRAEFIX);
  }

  /**
   * Laedt die Vertragsdaten eines Kunden (oder null, wenn keiner hinterlegt ist).
   */
  async getContract(customerId: string): Promise<CustomerContract | null> {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('vertrag_storage_path, vertrag_dateiname, vertrag_hochgeladen_am')
        .eq('id', customerId)
        .single();

      if (error || !data || !data.vertrag_storage_path) {
        return null;
      }

      return {
        storagePath: data.vertrag_storage_path,
        dateiname: data.vertrag_dateiname || 'Vertrag',
        hochgeladenAm: data.vertrag_hochgeladen_am
      };
    } catch (error) {
      console.error('[ContractService] getContract error:', error);
      return null;
    }
  }

  /**
   * Laedt einen Vertrag hoch und speichert die Metadaten am Kunden.
   * Ein vorhandener Vertrag wird ersetzt (altes Dokument wird aus dem Storage entfernt).
   * Ausnahme: aus dem Vertragswesen erzeugte Dokumente bleiben liegen.
   */
  async uploadContract(customerId: string, file: File): Promise<ContractActionResult> {
    try {
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > MAX_FILE_SIZE_MB) {
        return {
          success: false,
          error: `Datei zu groß (${fileSizeMB.toFixed(1)} MB). Maximum: ${MAX_FILE_SIZE_MB} MB.`
        };
      }

      // Bestehenden Pfad merken (zum spaeteren Entfernen).
      const existing = await this.getContract(customerId);

      // Neuer, eindeutiger Storage-Pfad.
      const storagePath = `${customerId}/${Date.now()}_${this.sanitizeFilename(file.name)}`;

      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('[ContractService] upload error:', uploadError);
        return { success: false, error: `Upload fehlgeschlagen: ${uploadError.message}` };
      }

      // Metadaten am Kunden speichern.
      const { error: updateError } = await supabase
        .from('customers')
        .update({
          vertrag_storage_path: storagePath,
          vertrag_dateiname: file.name,
          vertrag_hochgeladen_am: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', customerId);

      if (updateError) {
        // Rollback: gerade hochgeladene Datei wieder entfernen.
        await supabase.storage.from(BUCKET).remove([storagePath]);
        console.error('[ContractService] update error:', updateError);
        return { success: false, error: `Speichern fehlgeschlagen: ${updateError.message}` };
      }

      // Altes Dokument entfernen (best effort).
      // Aus dem Vertragswesen erzeugte Dokumente sind davon ausgenommen und
      // bleiben im Storage erhalten.
      if (
        existing?.storagePath &&
        existing.storagePath !== storagePath &&
        !this.istGenerierterVertrag(existing.storagePath)
      ) {
        await supabase.storage.from(BUCKET).remove([existing.storagePath]);
      }

      return { success: true };
    } catch (error) {
      console.error('[ContractService] uploadContract error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler beim Upload'
      };
    }
  }

  /**
   * Erstellt eine signierte Download-URL (1 Stunde gueltig).
   * Der Originalname wird als Download-Dateiname gesetzt.
   */
  async getDownloadUrl(storagePath: string, dateiname: string): Promise<string | null> {
    try {
      const { data, error } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(storagePath, 3600, { download: dateiname });

      if (error || !data?.signedUrl) {
        console.error('[ContractService] signed url error:', error);
        return null;
      }

      return data.signedUrl;
    } catch (error) {
      console.error('[ContractService] getDownloadUrl error:', error);
      return null;
    }
  }

  /**
   * Loescht den Vertrag eines Kunden aus Storage und DB.
   * Bei aus dem Vertragswesen erzeugten Dokumenten wird ausschliesslich der
   * Zeiger am Kunden geloest; die Datei bleibt im Storage und bleibt ueber
   * die Vertragshistorie erreichbar.
   */
  async deleteContract(customerId: string, storagePath: string): Promise<ContractActionResult> {
    try {
      if (!this.istGenerierterVertrag(storagePath)) {
        const { error: removeError } = await supabase.storage
          .from(BUCKET)
          .remove([storagePath]);

        if (removeError) {
          console.error('[ContractService] remove error:', removeError);
          return { success: false, error: `Löschen fehlgeschlagen: ${removeError.message}` };
        }
      }

      const { error: updateError } = await supabase
        .from('customers')
        .update({
          vertrag_storage_path: null,
          vertrag_dateiname: null,
          vertrag_hochgeladen_am: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', customerId);

      if (updateError) {
        console.error('[ContractService] delete update error:', updateError);
        return { success: false, error: `Speichern fehlgeschlagen: ${updateError.message}` };
      }

      return { success: true };
    } catch (error) {
      console.error('[ContractService] deleteContract error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler beim Löschen'
      };
    }
  }
}

// Singleton Export
export const contractService = new ContractService();
